/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @fullcalendar components
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";

// Material Dashboard 2 PRO React TS components
import { FC } from "react";

// Custom styles for Calendar
import CalendarRoot from "dashboard-template/examples/Calendar/CalendarRoot";

// Material Dashboard 2 PRO React context
import { useMaterialUIController } from "context/ThemeContext";
import Card from "./Card";
import Box from "./Box";
import { CalendarOptions, EventInput } from "@fullcalendar/core";
import { getAppointmentTimeRange } from "utils/date.utils";
import HtmlTooltip from "./HtmlTooltip";
import Typography from "./Typography";
import Stack from "@mui/material/Stack";

/**
 * Props for Calendar component extending FullCalendar options.
 */
export interface Props extends CalendarOptions {
  header?: {
    title?: string;
    date?: string;
  };
  comingEvents: EventInput[];
  locale?: string;
  [key: string]: any;
  allDayText?: string;
}

/**
 * Full-featured calendar component for appointment scheduling.
 * 
 * Features:
 * - Multiple view modes (day, week, month grids)
 * - Interactive event creation and editing via drag-and-drop
 * - Event click handlers for viewing details
 * - Time slot selection for new appointments
 * - Dark mode support with theme synchronization
 * - Custom event rendering with tooltips showing:
 *   - Appointment reason
 *   - Provider name
 *   - Patient name
 *   - Time range
 * - Color-coded events by status or category
 * - Localization support
 * 
 * Uses FullCalendar library with dayGrid, timeGrid, and interaction plugins.
 * 
 * @param header - Optional calendar header with title and date
 * @param comingEvents - Array of calendar events to display
 * @param rest - Additional FullCalendar options
 * @param locale - Language locale for the calendar (e.g., "en", "es").
 * @param allDayText - Text to display for all-day events
 * @returns Interactive calendar component
 */
const Calendar: FC<Props> = ({
  header = {
    title: "",
    date: "",
  },
  comingEvents,
  locale = "en",
  allDayText,
  ...rest
}: Props): JSX.Element => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const validClassNames = [
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "light",
    "dark",
  ];
  const events = comingEvents
    ? comingEvents.map((el: EventInput) => ({
        ...el,
        className: validClassNames.find((item) => item === el.className)
          ? `event-${el.className}`
          : "event-info",
        backgroundColor: el.backgroundColor,
      }))
    : [];

  const renderEventContent = (comingEvents) => {
    const backgroundColor =
      comingEvents.event.backgroundColor === "info"
        ? "transparent"
        : comingEvents.event.backgroundColor;
    return (
      <HtmlTooltip
        title={
          <Box px={1} className="fc-event-content">
            <Stack>{comingEvents.event.title}</Stack>
            <Stack>{comingEvents.event.extendedProps.doctor}</Stack>
            <Stack>{comingEvents.event.extendedProps.patient}</Stack>
            <Stack>
              {getAppointmentTimeRange(comingEvents.event.start, comingEvents.event.end)}
            </Stack>
          </Box>
        }
        placement="top"
      >
        <Stack
          className="data-event-calendar"
          style={{
            backgroundColor: backgroundColor,
          }}
        >
          <strong>{comingEvents.event.title}</strong>
          <Stack>{comingEvents.event.extendedProps.patient}</Stack>
          <Stack>{comingEvents.event.extendedProps.doctor}</Stack>
        </Stack>
      </HtmlTooltip>
    );
  };

  return (
    <Card sx={{ height: "100%" }}>
      <Box pt={header.title || header.date ? 2 : 0} px={2} lineHeight={1}>
        {header.title ? (
          <Typography variant="h6" fontWeight="medium" textTransform="capitalize">
            {header.title}
          </Typography>
        ) : null}
        {header.date ? (
          <Typography component="p" variant="button" color="text" fontWeight="regular">
            {header.date}
          </Typography>
        ) : null}
      </Box>

      <CalendarRoot p={2} ownerState={{ darkMode }}>
        <FullCalendar
          {...rest}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          events={events}
          height="100%"
          locale={locale}
          eventTimeFormat={{
            hour: "numeric",
            minute: "2-digit",
            hour12: false,
          }}
          slotLabelFormat={{
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }}
          allDayText={allDayText}
          eventContent={renderEventContent}
        />
      </CalendarRoot>
    </Card>
  );
};

export default Calendar;
